import type { ColumnDef, EnemyDef, LevelDef, PickupDef, SegmentDef } from '../../types';

// OP-403 La Frontera (GDD §10.6, §10.2, issue #135): the last level of the campaign, a night at the border wall, written by
// hand with the format of §15.6. 400 u of approach at 7.0 u/s (6.5 of world 4 plus 0.5 of level 3; takeoff, warm-up 18 %,
// tension 26 % up to CP-403-A at x 200, respite 15 %, peak 41 %), CP-403-B 5 u before the arena, the arena of JEFE-4 (open
// length: the fight ends it), the San Diego sky of 120 u with bonus bills and the jump zone. Columns: segments of the border
// wall (`w4-border-wall`, #126) with the 3.2 u gap of world 4 every 7 u, the gap centre never moving more than 2.5 u (the
// world allows 4.0). Hazards: the wall searchlights (EN-44, #130) in synchronized groups, the border drones (EN-42, #128), the
// law (EN-02) and a hitman plane (EN-03); no tunnel (EN-43: it is El Chato's). An armor before the checkpoint and a bribe in
// the respite; no scapular (it goes in OP-402). All x are local.

const column = (x: number, gapY: number): ColumnDef => ({ x, gapY, gapH: 3.2, skin: 'w4-border-wall' });
const dollar = (x: number, y: number): PickupDef => ({ x, y, type: 'dollar' });
const armor = (x: number, y: number): PickupDef => ({ x, y, type: 'armor' });
const bribe = (x: number, y: number): PickupDef => ({ x, y, type: 'bribe' });
/** A wall searchlight (EN-44) hanging high; the lights of a group sweep in step. */
const light = (x: number, syncGroup: number): EnemyDef => ({ x, y: 10.5, type: 'EN-44', params: { syncGroup } });
/** A border drone (EN-42) patrolling vertically between two heights. */
const drone = (x: number, yMin: number, yMax: number): EnemyDef => ({ x, y: (yMin + yMax) / 2, type: 'EN-42', params: { yMin, yMax, dir: 1 } });
/** A helicopter of the law (EN-02), hovering high. */
const law = (x: number, y: number): EnemyDef => ({ x, y, type: 'EN-02' });
/** A hitman plane (EN-03), announced with INTERCEPT. */
const hitman = (x: number, y: number): EnemyDef => ({ x, y, type: 'EN-03' });

const takeoff: SegmentDef = { id: 'w4-frontera-takeoff-01', kind: 'takeoff', length: 40, columns: [], enemies: [], pickups: [] };

const warmup01: SegmentDef = {
  id: 'w4-frontera-warmup-01',
  kind: 'pattern',
  phase: 'warmup',
  length: 66,
  columns: [
    column(20.0, 6.0),
    column(27.0, 6.5),
    column(34.0, 6.0),
    column(41.0, 5.5),
    column(48.0, 6.0),
    column(55.0, 6.5),
    column(62.0, 6.0),
  ],
  enemies: [],
  pickups: [
    dollar(29.50, 6.25),
    dollar(30.50, 6.55),
    dollar(31.50, 6.25),
    dollar(43.50, 5.75),
    dollar(44.50, 6.05),
    dollar(45.50, 5.75),
  ],
};

const tension01: SegmentDef = {
  id: 'w4-frontera-tension-01',
  kind: 'pattern',
  phase: 'tension',
  length: 47,
  columns: [
    column(3.0, 7.0),
    column(10.0, 6.0),
    column(17.0, 7.5),
    column(24.0, 6.0),
    column(31.0, 8.0),
    column(38.0, 6.5),
    column(45.0, 7.0),
  ],
  enemies: [light(20.5, 1), light(34.5, 1)],
  pickups: [
    dollar(12.50, 6.75),
    dollar(13.50, 7.05),
    dollar(14.50, 6.75),
    dollar(26.50, 7.00),
    dollar(27.50, 7.30),
    dollar(28.50, 7.00),
  ],
};

const tension02: SegmentDef = {
  id: 'w4-frontera-tension-02',
  kind: 'pattern',
  phase: 'tension',
  length: 47,
  columns: [
    column(5.0, 6.0),
    column(12.0, 7.5),
    column(19.0, 5.5),
    column(26.0, 7.0),
    column(33.0, 5.5),
    column(40.0, 7.5),
  ],
  enemies: [drone(15.5, 4.0, 8.0)],
  pickups: [
    dollar(14.50, 6.50),
    dollar(15.50, 6.80),
    dollar(16.50, 6.50),
    dollar(28.50, 6.25),
    dollar(29.50, 6.55),
    dollar(30.50, 6.25),
    armor(8.5, 6.75),
  ],
};

const respite01: SegmentDef = {
  id: 'w4-frontera-respite-01',
  kind: 'pattern',
  phase: 'respite',
  length: 54,
  columns: [
    column(4.0, 6.0),
    column(11.0, 6.5),
    column(18.0, 6.0),
    column(25.0, 6.5),
    column(32.0, 6.0),
    column(39.0, 6.5),
    column(46.0, 6.0),
  ],
  enemies: [],
  pickups: [
    dollar(13.50, 6.25),
    dollar(14.50, 6.55),
    dollar(15.50, 6.25),
    dollar(27.50, 6.25),
    dollar(28.50, 6.55),
    dollar(29.50, 6.25),
    bribe(14.5, 6.25),
  ],
};

const peak01: SegmentDef = {
  id: 'w4-frontera-peak-01',
  kind: 'pattern',
  phase: 'peak',
  length: 73,
  columns: [
    column(2.0, 7.5),
    column(9.0, 5.5),
    column(16.0, 7.5),
    column(23.0, 5.0),
    column(30.0, 7.0),
    column(37.0, 4.5),
    column(44.0, 7.0),
    column(51.0, 5.0),
    column(58.0, 7.5),
    column(65.0, 6.0),
  ],
  enemies: [light(12.5, 1), light(26.5, 1), drone(33.5, 4.0, 9.0), law(47.5, 11.2)],
  pickups: [
    dollar(11.50, 6.50),
    dollar(12.50, 6.80),
    dollar(13.50, 6.50),
    dollar(25.50, 6.00),
    dollar(26.50, 6.30),
    dollar(27.50, 6.00),
  ],
};

const peak02: SegmentDef = {
  id: 'w4-frontera-peak-02',
  kind: 'pattern',
  phase: 'peak',
  length: 73,
  columns: [
    column(2.0, 7.5),
    column(9.0, 5.5),
    column(16.0, 7.0),
    column(23.0, 5.0),
    column(30.0, 7.0),
    column(37.0, 5.5),
    column(44.0, 7.5),
  ],
  enemies: [drone(12.5, 3.5, 8.5), light(20.5, 2), light(34.5, 2), hitman(37.0, 9.0)],
  pickups: [
    dollar(11.50, 6.25),
    dollar(12.50, 6.55),
    dollar(13.50, 6.25),
    dollar(25.50, 6.00),
    dollar(26.50, 6.30),
    dollar(27.50, 6.00),
  ],
};

/** The arena of JEFE-4: no gap columns outside the closing columns of El Muro; the fight lasts until the boss retreats (§9.1). */
const arena: SegmentDef = { id: 'w4-frontera-arena-01', kind: 'boss-arena', length: 600, columns: [], enemies: [], pickups: [] };

/** The sky of San Diego (§11.1): 120 u of free flight over the bay with bonus bills. */
const usSky: SegmentDef = {
  id: 'w4-frontera-us-sky-01',
  kind: 'us-sky',
  length: 120,
  columns: [],
  enemies: [],
  pickups: [dollar(20, 6), dollar(21, 6.4), dollar(22, 6.8), dollar(60, 8), dollar(61, 8.4), dollar(62, 8.8), dollar(100, 6), dollar(101, 5.6), dollar(102, 5.2)],
};

/** The jump zone of El Salto (§11.1 steps 2-6): the X on the ground and room for the fall. */
const jumpZone: SegmentDef = { id: 'w4-frontera-jump-01', kind: 'jump', length: 60, columns: [], enemies: [], pickups: [] };

export const OP_403_SEGMENTS: readonly SegmentDef[] = [takeoff, warmup01, tension01, tension02, respite01, peak01, peak02, arena, usSky, jumpZone];

export const OP_403: LevelDef = {
  id: '4-3',
  op: 'OP-403',
  name: 'La Frontera',
  world: 4,
  scrollSpeed: 7.0,
  timeOfDay: 'night',
  segments: OP_403_SEGMENTS.map((segment) => segment.id),
  checkpoints: [
    { id: 'CP-403-A', x: 200 },
    { id: 'CP-403-B', x: 395 },
  ],
  ending: 'boss-and-jump',
  bossId: 'JEFE-4',
};
